import { environments, formatNumber, pathname } from '../../utils';
import RejectEditedPlanDialog from './RejectEditedPlanDialog';
import Typography from '@material-ui/core/Typography';
import AdminFreeText from '../Dashboard/Requests/AnsweredRequests/AdminFreeText';
import React from 'react';
import Link from '@material-ui/core/Link';
import Contract from './Contract';
import EmailSupport from '../BuildingBlocks/Misc/EmailSupport';
import ExpirationWarning from './PlanDiscovery/ExpirationWarning';
import PendingPlanNote from './PendingPlanNote';
import {
  AcceptAndActiveButton,
  DeclineButton,
  ProposeAlternativeButton,
} from './SummaryActionButtons';
import ProposeAlternativeReasonDialog from './ProposeAlternativeReasonDialog';

function FinancialDiffRow({ financialValue, financialText, classes }) {
  const subtractOrAdd = (payment) => {
    if (payment > 0) return 'added to';
    else return 'subtracted from';
  };

  return (
    <Typography variant="subtitle2" paragraph className={classes.zeroMargin}>
      ${formatNumber(Math.abs(Math.round(financialValue)))}{' '}
      {subtractOrAdd(Math.round(financialValue))} {financialText}
    </Typography>
  );
}

function SummaryFinancialDiff({ plan, classes }) {
  return (
    <>
      {plan?.projected_revenue !== 0 && (
        <FinancialDiffRow
          financialValue={plan?.projected_revenue}
          financialText={'revenue'}
          classes={classes}
        />
      )}
      {plan?.COGS_change !== 0 && (
        <FinancialDiffRow
          financialValue={plan?.COGS_change}
          financialText={'COGS'}
          classes={classes}
        />
      )}
      {plan?.cost_of_capital_change !== 0 && (
        <FinancialDiffRow
          financialValue={plan?.cost_of_capital_change}
          financialText={'Cost of Capital'}
          classes={classes}
        />
      )}
      {plan?.total_participation_change !== 0 && (
        <FinancialDiffRow
          financialValue={plan?.total_participation_change}
          financialText={'Participation'}
          classes={classes}
        />
      )}
    </>
  );
}

function SummaryPendingCustomerSuggestButtons({
  plan,
  rejectEditedPlan,
  openContractAddendum,
  classes,
}) {
  return (
    <div className={classes.buttonSection}>
      <RejectEditedPlanDialog plan={plan} rejectEditedPlan={rejectEditedPlan} />
      <AcceptAndActiveButton openContractAddendum={openContractAddendum} />
      <DeclineButton />
    </div>
  );
}

function SummaryPendingNewAdminSuggestButtons({
  plan,
  openContractAddendum,
  classes,
}) {
  return (
    <div className={classes.buttonSection}>
      <ProposeAlternativeReasonDialog plan={plan} />
      <AcceptAndActiveButton openContractAddendum={openContractAddendum} />
      <ProposeAlternativeButton />
    </div>
  );
}

export function SummaryPendingNewApp({
  plan,
  rejectEditedPlan,
  openContract,
  classes,
}) {
  let isProduction = process.env.REACT_APP_ENV === environments.prod;
  const mainHostName = 'app.8fig.co';
  let expiresInHeader = 'A few days Remaining';
  if (plan?.expiresIn !== null) {
    if (plan?.expiresIn < 0) {
      expiresInHeader = 'Expires soon';
    } else {
      expiresInHeader = `${plan.expiresIn} ${
        plan.expires === 1 ? 'day' : 'days'
      } Remaining`;
    }
  }

  return (
    <>
      <RejectEditedPlanDialog plan={plan} rejectEditedPlan={rejectEditedPlan} />
      <div className={classes.buttonSection}>
        <>
          <Typography variant="h4" paragraph>
            {plan?.contractSigned ? 'Agreement signed' : 'Ready to Go?'}
          </Typography>
          {isProduction && window.location.hostname !== mainHostName && (
            <>
              <Typography paragraph>
                It looks like you're signed into our old domain.
              </Typography>
              <Typography paragraph>
                To sign your agreement, you must be signed into{' '}
                <Link
                  href={`https://${mainHostName}${pathname.plans.url}`}
                  target="_blank"
                >{`https://${mainHostName}`}</Link>
                .
              </Typography>
            </>
          )}
          {(!isProduction ||
            (isProduction && window.location.hostname === mainHostName)) && (
            <>
              {plan?.contractSigned && (
                <div className={classes.section}>
                  <Typography>
                    Thanks for signing! Our team will review your signed
                    agreement and activate your Growth Plan in the next business
                    day.
                  </Typography>
                </div>
              )}
              {!plan?.contractSigned && (
                <>
                  <div className={classes.section}>
                    <Contract openContract={openContract} />
                  </div>
                  <DeclineButton />
                  <Typography variant="body1" paragraph>
                    Questions? <EmailSupport text="Contact us >" fontSize={1} />
                  </Typography>
                  <Typography
                    variant="h4"
                    paragraph
                    className={classes.spacing}
                  >
                    {expiresInHeader}
                  </Typography>
                  <div className={classes.section}>
                    <ExpirationWarning />
                  </div>
                </>
              )}
            </>
          )}
        </>
        <PendingPlanNote />
      </div>
    </>
  );
}

export function SummaryPendingHasOriginApp({
  plan,
  rejectEditedPlan,
  openContractAddendum,
  classes,
}) {
  return (
    <>
      <Typography variant="h6" className={classes.bold}>
        Please review your recalculated Growth Plan below.
      </Typography>
      <Typography className={classes.spacing} variant="subtitle2" gutterBottom>
        Once you Accept {'&'} Activate, the Funds Delivery Schedule below will
        be activated and Growth Plan {plan.originDisplayName} will be archived.
      </Typography>
      <Typography
        variant="h6"
        paragraph
        className={`${classes.zeroMargin} ${classes.bold} ${classes.spacing}`}
      >
        Summary of the changes to your plan:
      </Typography>
      <SummaryFinancialDiff plan={plan} classes={classes} />
      {plan?.admin_free_text && (
        <AdminFreeText freeText={plan?.admin_free_text} />
      )}
      {plan?.createdByAdmin ? (
        <>
          <SummaryPendingNewAdminSuggestButtons
            plan={plan}
            openContractAddendum={openContractAddendum}
            classes={classes}
          />
          <Typography
            className={classes.spacing}
            variant="subtitle2"
            gutterBottom
          >
            To request a different Plan, you may send a message to our team via
            the 'Propose Alternative' button.
          </Typography>
        </>
      ) : (
        <>
          <SummaryPendingCustomerSuggestButtons
            plan={plan}
            rejectEditedPlan={rejectEditedPlan}
            openContractAddendum={openContractAddendum}
            classes={classes}
          />
          <Typography
            className={classes.spacing}
            variant="subtitle2"
            gutterBottom
          >
            If you do not take action, this Plan will be archived and your added
            Lines will return to 'Draft'. You'll be able to re-submit these
            Lines at any time.
          </Typography>
        </>
      )}
    </>
  );
}
